import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../core/i18n.service';
import { TeamsApiService } from '../core/teams-api.service';
import { PlayerTeamSummary } from '../core/teams-api.types';

@Component({
  selector: 'app-player-teams-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <section class="page">
      <header class="head">
        <h1>{{ t('player.teams.title') }}</h1>
        <div class="head-actions">
          <a class="cta cta--secondary" routerLink="/tournaments">{{ t('player.teams.openTournaments') }}</a>
          <a class="cta" routerLink="/player/teams/create">{{ t('player.teams.create') }}</a>
        </div>
      </header>

      @if (loading()) {
        <p class="state">{{ t('player.teams.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else if (teams().length === 0) {
        <article class="empty">
          <p><strong>{{ t('player.teams.none.title') }}</strong></p>
          <p class="state">{{ t('player.teams.none.subtitle') }}</p>
          <p><a routerLink="/player/teams/create">{{ t('player.teams.create') }}</a></p>
        </article>
      } @else {
        <ul class="list">
          @for (team of teams(); track team.id) {
            <li class="card">
              <div class="card-head">
                <p class="team-name">{{ team.name }}</p>
                <span class="badge">{{ team.member_count }} / {{ team.max_members }}</span>
              </div>
              <p class="meta">{{ t('player.teams.myRole', { value: team.my_role || t('player.teamDetail.role.player').toLowerCase() }) }}</p>
              <p class="meta meta--status">{{ team.can_manage_roster ? t('player.teams.manageable') : t('player.teams.notManageable') }}</p>
              @if (team.logo_url) {
                <p class="meta">{{ t('player.teams.logo', { value: team.logo_url }) }}</p>
              }
              <div class="actions">
                <a [routerLink]="['/player/teams', team.id]">{{ t('player.teams.viewRoster') }}</a>
              </div>
            </li>
          }
        </ul>
      }
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 860px; margin: 0 auto; }
    .head { display: flex; align-items: center; justify-content: space-between; gap: 0.8rem; margin-bottom: 0.85rem; }
    .head-actions { display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap; justify-content: end; }
    h1 { margin: 0; font-size: 1.45rem; color: var(--text-strong); }
    .cta { border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.5rem 0.75rem; text-decoration: none; color: #091325; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); font-weight: 700; }
    .cta--secondary { color: #d8e8ff; background: rgba(101, 145, 216, 0.18); border-color: rgba(112, 164, 255, 0.42); }
    .list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.75rem; }
    .card { border: 1px solid var(--line-soft); border-radius: 12px; padding: 0.8rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    .card-head { display: flex; justify-content: space-between; align-items: center; gap: 0.6rem; margin-bottom: 0.2rem; }
    .team-name { margin: 0; color: var(--text-strong); font-weight: 700; font-size: 1.02rem; }
    .badge { border: 1px solid rgba(138, 184, 255, 0.45); border-radius: 999px; padding: 0.2rem 0.55rem; color: #dce9ff; font-size: 0.78rem; }
    .meta { margin: 0.25rem 0; color: var(--text-muted); }
    .meta--status { color: #d8e8ff; font-size: 0.85rem; }
    .actions { margin-top: 0.55rem; }
    .actions a { text-decoration: none; color: var(--accent); font-weight: 700; }
    .empty { border: 1px solid var(--line-soft); border-radius: 12px; padding: 0.8rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    .state { color: var(--text-muted); }
    .state--error { color: var(--danger); }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
  `
})
export class PlayerTeamsPageComponent {
  private readonly teamsApi = inject(TeamsApiService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly teams = signal<PlayerTeamSummary[]>([]);
  protected readonly loading = signal(true);
  protected readonly errorMessage = signal<string | null>(null);

  constructor() {
    this.loadTeams();
  }

  private loadTeams(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.teamsApi
      .getMyTeams()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ teams }) => {
          this.teams.set(teams);
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set(this.t('player.teams.loadError'));
          this.loading.set(false);
        }
      });
  }
  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
